import React, { FC, useLayoutEffect, useState } from 'react';
import { IcLogo40 } from '$assets';
import {
  Block,
  Button,
  Form,
  FormItem,
  Header,
  InlineHeader,
  Input,
  Placeholder,
  ScrollView,
} from '$uikit';
import { Loader } from '$shared/components';
import { Api } from '$api';
import { useDispatch } from 'react-redux';
import { toastActions } from '$store/toast';
import { useTranslator } from '$hooks';
import { SystemPromoCodeModel } from '$store/models';
import { format, formatDate } from '$utils';
import s from './SystemPromoCode.module.sass';
import { useRouter } from 'react-router5';
import { VERIFICATION_LANDING } from '$shared/constants/pages';

export const SystemPromoCode: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const router = useRouter();
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [items, setItems] = useState<SystemPromoCodeModel[]>([]);
  const [code, setCode] = useState('');

  const loadData = () => {
    setLoadingStatus('loading');
    Api.get<any, any>('/promo_code/system')
      .then((data) => {
        setItems(data.items);
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  };

  useLayoutEffect(() => {
    loadData();
  }, []);

  const handleActivate = () => {
    const codePrepared = code.trim().toLowerCase();
    if (codePrepared.length === 0) {
      dispatch(toastActions.setToastFail('Enter a code!'));
      return;
    }

    dispatch(toastActions.setToastLoading());
    Api.post('/promo_code/system/activate', {
      code: codePrepared,
    })
      .then(() => {
        dispatch(toastActions.setToastSuccess());
        setCode('');
        loadData();
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));

        if (e.code === 'verification_required') {
          router.navigate(VERIFICATION_LANDING);
        }
      });
  };

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
          centered
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <>
        <div>
          <Form>
            <FormItem title="Activate a promo code">
              <Input
                placeholder="Enter a code..."
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </FormItem>
            <FormItem>
              <Button onClick={handleActivate}>Activate</Button>
            </FormItem>
          </Form>
        </div>
        <InlineHeader>Activation history</InlineHeader>
        {items.length === 0 && (
          <Placeholder title="Empty" compact>
            {`You haven't activated any promo codes yet.`}
          </Placeholder>
        )}
        <div>
          {items.map((item) => {
            return (
              <Block key={item.id} className={s.historyItem}>
                <div className={s.historyItemHeader}>
                  <div className={s.historyItemCode}>{item.code}</div>
                  <div className={s.historyItemDate}>
                    {formatDate(new Date(item.createdAt * 1000), t)}
                  </div>
                </div>
                <div className={s.historyItemTitle}>{item.title}</div>
                <div className={s.historyItemDescription}>{item.description}</div>
              </Block>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <>
      <Header>
        <div className="PromoCode__header">
          <IcLogo40 />
          <span>Ton.Place</span>
        </div>
      </Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
